"use client";

import useEmblaCarousel from "embla-carousel-react";
import React from "react";
import type { StoreApi } from "zustand";
import { createStore, useStore } from "zustand";
import type { ProviderProps } from "@/lib/types";
import type { CarouselStore } from "../carousel-store";
import { getMountedSlides } from "../helpers";
import type {
  CarouselApi,
  CarouselOptions,
  CarouselPlugins,
  CarouselRef,
} from "../types";

export type CarouselContextType = {
  store: StoreApi<CarouselStore>;
  emblaRef: CarouselRef;
  emblaApi: CarouselApi;
};

export const CarouselContext = React.createContext<CarouselContextType | null>(
  null,
);

export function useCarousel(): CarouselContextType {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error("useCarousel must be used within CarouselProvider");
  }

  return context;
}

export function useCarouselStore(selector: (state: CarouselStore) => any) {
  const { store } = useCarousel();

  return useStore(store, selector);
}

export type CarouselProviderProps = ProviderProps<{
  options?: CarouselOptions;
  plugins?: CarouselPlugins;
  orientation?: "horizontal" | "vertical";
  neighborSlidesOffset?: number;
  setApi?: (emblaApi: CarouselApi) => void;
}>;

export default function CarouselProvider({
  options,
  plugins,
  orientation = "horizontal",
  neighborSlidesOffset = 1,
  setApi,
  children,
}: CarouselProviderProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { ...options, axis: orientation === "horizontal" ? "x" : "y" },
    plugins,
  );

  const [store] = React.useState(() =>
    createStore<CarouselStore>((set, get) => ({
      canScrollNext: false,
      canScrollPrev: false,
      canScrollFirst: false,
      canScrollLast: false,
      loop: options?.loop ?? false,
      neighborSlidesOffset,
      orientation,
      scrollSnapList: [],
      slidesInView: new Set<number>(),
      mountedSlides: new Set<number>(),
      selectedSlide: 0,
      totalSlides: 0,
      scrollTo: () => {},
      scrollNext: () => {},
      scrollPrev: () => {},
      scrollFirst: (...args) => get().scrollTo(0, ...args),
      scrollLast: (...args) => get().scrollTo(get().totalSlides - 1, ...args),
      setScrollTo: scrollTo => set({ scrollTo }),
      setScrollNext: scrollNext => set({ scrollNext }),
      setScrollPrev: scrollPrev => set({ scrollPrev }),
      setScrollSnapList: scrollSnapList => set({ scrollSnapList }),
      setTotalSlides: totalSlides => set({ totalSlides }),
      setSelectedSlide: selectedSlide => set({ selectedSlide }),
      setCanScrollNext: canScrollNext => set({ canScrollNext }),
      setCanScrollPrev: canScrollPrev => set({ canScrollPrev }),
    })),
  );

  const handleOnInit = React.useCallback(
    (api: CarouselApi) => {
      if (!api) {
        return;
      }

      store.setState({
        scrollTo: api.scrollTo,
        scrollNext: api.scrollNext,
        scrollPrev: api.scrollPrev,
      });
    },
    [store.setState],
  );

  const handleOnSelect = React.useCallback(
    (api: CarouselApi) => {
      if (!api) {
        return;
      }

      const scrollSnapList = api.scrollSnapList();

      store.setState({
        scrollSnapList,
        totalSlides: scrollSnapList.length,
        selectedSlide: api.selectedScrollSnap(),
        canScrollNext: api.canScrollNext(),
        canScrollPrev: api.canScrollPrev(),
      });
    },
    [store.setState],
  );

  const handleOnSlidesInView = React.useCallback(
    (api: CarouselApi) => {
      if (!api) {
        return;
      }

      const next = new Set(api.slidesInView());

      if (next.size === 0) {
        return;
      }

      store.setState(prev => {
        const mountedSlides = getMountedSlides(
          next,
          prev.neighborSlidesOffset,
          prev.totalSlides,
          prev.loop,
        );

        return {
          slidesInView: next,
          mountedSlides,
          canScrollFirst: !next.has(0),
          canScrollLast:
            prev.totalSlides > 0 && !next.has(prev.totalSlides - 1),
        };
      });
    },
    [store.setState],
  );

  //
  // Wire tore to embla 'select'
  //
  React.useEffect(() => {
    if (!emblaApi) {
      return;
    }

    handleOnInit(emblaApi);
    handleOnSelect(emblaApi);
    handleOnSlidesInView(emblaApi);

    emblaApi.on("select", handleOnSelect);
    emblaApi.on("reInit", handleOnSelect);
    emblaApi.on("slidesInView", handleOnSlidesInView);

    return () => {
      emblaApi.off("select", handleOnSelect);
      emblaApi.off("reInit", handleOnSelect);
      emblaApi.off("slidesInView", handleOnSlidesInView);
    };
  }, [emblaApi, handleOnInit, handleOnSelect, handleOnSlidesInView]);

  // Expose, API
  React.useEffect(() => {
    if (!emblaApi || !setApi) {
      return;
    }

    setApi(emblaApi);
  }, [setApi, emblaApi]);

  //
  // Context
  //
  const context = React.useMemo(
    () => ({ store, emblaRef, emblaApi }),
    [store, emblaRef, emblaApi],
  );

  return (
    <CarouselContext.Provider value={context}>
      {children}
    </CarouselContext.Provider>
  );
}
